import { useState } from "react";
import { Share2, Link2, QrCode, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/useToast";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { QRCodeDialog } from "@/components/QRCodeDialog";

// X (Twitter) icon
function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// Nostr icon
function NostrIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 256 256" className={className} fill="currentColor">
      <path d="M128 0C57.308 0 0 57.308 0 128s57.308 128 128 128 128-57.308 128-128S198.692 0 128 0zm45.781 178.125c-11.094 11.094-29.375 11.094-40.469 0L85.219 130.03c-11.094-11.093-11.094-29.374 0-40.468 11.094-11.094 29.375-11.094 40.469 0l48.093 48.094c11.094 11.094 11.094 29.375 0 40.469z" />
    </svg>
  );
}

interface ShareDropdownProps {
  url: string;
  title?: string;
  description?: string;
  className?: string;
}

export function ShareDropdown({
  url,
  title = "Check out my ZapMeACoffee page!",
  description,
  className
}: ShareDropdownProps) {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isQROpen, setIsQROpen] = useState(false);
  const { toast } = useToast();
  const { mutate: createEvent } = useNostrPublish();
  const { user } = useCurrentUser();

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast({ title: "Link copied! 📋" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Failed to copy",
        description: "Could not copy link to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleShareToX = () => {
    const text = description || title;
    const tweetUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(tweetUrl, "_blank", "noopener,noreferrer,width=550,height=420");
  };

  const handleShareToNostr = () => {
    if (!user) {
      toast({
        title: "Login required",
        description: "Please log in to share on Nostr",
        variant: "destructive",
      });
      return;
    }

    const content = `${description || title}\n\n${url}`;

    createEvent(
      {
        kind: 1,
        content,
      },
      {
        onSuccess: () => {
          toast({ title: "Shared on Nostr! ⚡" });
          setIsOpen(false);
        },
        onError: () => {
          toast({
            title: "Failed to share",
            description: "Could not post to Nostr",
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleShowQR = () => {
    setIsOpen(false);
    setIsQROpen(true);
  };

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            className={className}
          >
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
            {copied ? "Copied!" : "Share Page"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          className="w-56 rounded-xl border-0 shadow-xl shadow-black/10 p-2"
        >
          <DropdownMenuItem
            onClick={handleCopyLink}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer focus:bg-secondary/80"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-secondary">
              <Link2 className="h-4 w-4 text-muted-foreground" />
            </div>
            <span className="font-medium">Copy Link</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator className="my-2" />

          <DropdownMenuItem
            onClick={handleShareToX}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer focus:bg-secondary/80"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-black">
              <XIcon className="h-4 w-4 text-white" />
            </div>
            <span className="font-medium">Share to X</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={handleShareToNostr}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer focus:bg-secondary/80"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600">
              <NostrIcon className="h-4 w-4 text-white" />
            </div>
            <span className="font-medium">Share to Nostr</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator className="my-2" />

          <DropdownMenuItem
            onClick={handleShowQR}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer focus:bg-secondary/80"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-secondary">
              <QrCode className="h-4 w-4 text-muted-foreground" />
            </div>
            <span className="font-medium">Show QR</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* QR Code Dialog */}
      <QRCodeDialog
        open={isQROpen}
        onOpenChange={setIsQROpen}
        url={url}
        title="Scan to open my page"
      />
    </>
  );
}
